/**
 * Responsive utility functions for mobile-first design
 */

import breakpoints from "../base/breakpoints";

/**
 * Get responsive values based on breakpoints
 * @param {Object} values - Object with breakpoint keys and values
 * @returns {Object} - Responsive styles object
 */
function responsive(values) {
  const styles = {};
  
  Object.entries(values).forEach(([breakpoint, value]) => {
    if (breakpoint === 'xs') {
      Object.assign(styles, value);
    } else {
      const mediaQuery = `@media (min-width: ${breakpoints.values[breakpoint]}px)`;
      styles[mediaQuery] = value;
    }
  });
  
  return styles;
}

/**
 * Hide element on specific breakpoints
 * @param {Array} breakpoints - Array of breakpoint names to hide on
 * @returns {Object} - CSS styles
 */
function hideOn(breakpointList) {
  const styles = {};
  
  breakpointList.forEach(bp => {
    if (bp === 'xs') {
      styles.display = 'none';
    } else {
      const mediaQuery = `@media (min-width: ${breakpoints.values[bp]}px)`;
      styles[mediaQuery] = { display: 'none' };
    }
  });
  
  return styles;
}

/**
 * Show element only on specific breakpoints
 * @param {Array} breakpoints - Array of breakpoint names to show on
 * @returns {Object} - CSS styles
 */
function showOn(breakpointList) {
  const styles = { display: 'none' };
  
  breakpointList.forEach(bp => {
    if (bp === 'xs') {
      styles.display = 'block';
    } else {
      const mediaQuery = `@media (min-width: ${breakpoints.values[bp]}px)`;
      styles[mediaQuery] = { display: 'block' };
    }
  });
  
  return styles;
}

/**
 * Mobile-first spacing utilities
 */
const spacing = {
  mobile: {
    xs: '0.25rem',
    sm: '0.5rem', 
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  },
  tablet: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem', 
    lg: '2rem',
    xl: '3rem'
  },
  desktop: {
    xs: '1rem',
    sm: '1.5rem',
    md: '2rem',
    lg: '3rem', 
    xl: '4rem'
  }
};

/**
 * Get responsive spacing
 * @param {string} size - Size key (xs, sm, md, lg, xl)
 * @returns {Object} - Responsive spacing styles
 */
function getSpacing(size) {
  return responsive({
    xs: { padding: spacing.mobile[size] },
    md: { padding: spacing.tablet[size] },
    lg: { padding: spacing.desktop[size] }
  });
}

/**
 * Mobile touch-friendly button sizes
 */
const touchSizes = {
  small: {
    minHeight: '44px',
    minWidth: '44px',
    padding: '8px 16px'
  },
  medium: {
    minHeight: '48px', 
    minWidth: '48px',
    padding: '12px 24px'
  },
  large: {
    minHeight: '56px',
    minWidth: '56px', 
    padding: '16px 32px'
  }
};

/**
 * Container max-widths for different screen sizes
 */
const containerSizes = {
  mobile: '100%',
  tablet: '768px',
  desktop: '1200px',
  wide: '1400px'
};

/**
 * Get responsive container styles
 * @returns {Object} - Container styles
 */
function getContainer() {
  return responsive({
    xs: { 
      width: '100%',
      maxWidth: containerSizes.mobile,
      paddingLeft: '16px',
      paddingRight: '16px'
    },
    sm: {
      paddingLeft: '24px', 
      paddingRight: '24px'
    },
    md: {
      maxWidth: containerSizes.tablet,
      paddingLeft: '32px',
      paddingRight: '32px'
    },
    lg: {
      maxWidth: containerSizes.desktop
    },
    xl: {
      maxWidth: containerSizes.wide
    }
  });
}

/**
 * Mobile-optimized font sizes
 */
const mobileFontSizes = {
  h1: {
    xs: '1.75rem',  // 28px
    md: '2.125rem', // 34px
    lg: '2.5rem'    // 40px
  },
  h2: {
    xs: '1.5rem',   // 24px
    md: '1.875rem', // 30px
    lg: '2.25rem'   // 36px
  },
  h3: {
    xs: '1.25rem',  // 20px
    md: '1.5rem',   // 24px
    lg: '1.875rem'  // 30px
  },
  h4: {
    xs: '1.125rem', // 18px
    md: '1.25rem',  // 20px
    lg: '1.5rem'    // 24px
  },
  h5: {
    xs: '1rem',     // 16px
    md: '1.125rem', // 18px
    lg: '1.25rem'   // 20px
  },
  h6: {
    xs: '0.875rem', // 14px
    md: '1rem',     // 16px
    lg: '1.125rem'  // 18px
  },
  body1: {
    xs: '0.875rem', // 14px
    md: '1rem',     // 16px
    lg: '1rem'      // 16px
  },
  body2: {
    xs: '0.75rem',  // 12px
    md: '0.875rem', // 14px
    lg: '0.875rem'  // 14px
  }
};

/**
 * Get responsive font size
 * @param {string} variant - Typography variant
 * @returns {Object} - Responsive font size styles
 */
function getFontSize(variant) {
  const sizes = mobileFontSizes[variant];
  if (!sizes) return {};
  
  return responsive({
    xs: { fontSize: sizes.xs },
    md: { fontSize: sizes.md },
    lg: { fontSize: sizes.lg }
  });
}

/**
 * Mobile-specific media queries
 */
const mediaQueries = {
  mobile: `@media (max-width: ${breakpoints.values.sm - 1}px)`,
  tablet: `@media (min-width: ${breakpoints.values.sm}px) and (max-width: ${breakpoints.values.lg - 1}px)`,
  desktop: `@media (min-width: ${breakpoints.values.lg}px)`,
  touch: '@media (hover: none) and (pointer: coarse)',
  hover: '@media (hover: hover) and (pointer: fine)'
};

export {
  responsive,
  hideOn,
  showOn,
  spacing,
  getSpacing,
  touchSizes,
  containerSizes,
  getContainer,
  mobileFontSizes,
  getFontSize,
  mediaQueries
};